import {
  CheckOutlined,
  CloseOutlined,
  PlusOutlined,
  ReloadOutlined,
  RightOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { Avatar, Button, Col, Divider, Drawer, Flex, Input, Select, Spin, Tag, Typography } from "antd";
import dayjs from "dayjs";
import type { BaseSelectRef } from "rc-select";
import type { ReactNode, UIEvent } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks/redux-hooks";
import { useIsMobileView } from "../../hooks/ui-hook";
import { useClientBrowse } from "../../hooks/useClientBrowse";
import { useClientSearch } from "../../hooks/useClientSearch";
import { useResolveClient } from "../../hooks/useResolveClient";
import useSpecialityReference from "../../hooks/useSpecialityReference";
import { setAddClientView } from "../../slices/appStateSlice";
import { setRecordingDetail } from "../../slices/recordingsSlice";
import type { TClientForm } from "../../types/index.type";
import { scheduleSlotLabel } from "../../utils/clientSchedule";
import { getRandomAvatarColor } from "../../utils/ui.utils";
import PickerListFooter from "../ClientPicker/PickerListFooter.component";
import { cooldownLabel } from "../EhrSyncButton/copy";
import styles from "./createnotemodal.module.scss";
import { type PatientPickSource, type ScheduleGroup, buildScheduleGroups, syncedLabel } from "./schedulePick.utils";
import { useSchedulePickContext } from "./useSchedulePick";

type ClientOption = {
  value: number;
  optionLabel: string;
  label: ReactNode;
  secondaryDetails: string[];
  isOwned?: boolean;
  ownerName?: string;
  client: TClientForm;
  source: PatientPickSource;
  rank: number;
  /** "10:30 AM" for a scheduled row, "" otherwise. */
  slotLabel: string;
  /** "note 10:22 AM" once this clinician has documented the patient today. */
  noteLabel: string;
  /** Recommended rows carry their time in bold. */
  emphasizeSlot: boolean;
  picked: boolean;
  muted: boolean;
};

/** An antd option group: a schedule section (Recommended, Up next, ...) or the roster remainder. */
type ClientOptionGroup = { key: string; label: ReactNode; options: ClientOption[] };
type SelectOption = ClientOption | ClientOptionGroup;

const initialsOf = (name: string) =>
  name
    .split(" ")
    .filter((word) => word.length > 0)
    .map((word) => word.charAt(0).toUpperCase())
    .slice(0, 2)
    .join("");

const GroupHeader = ({ title, note }: { title: string; note: string }) => (
  <Flex
    justify="space-between"
    align="center"
    gap={8}
    style={{
      fontSize: 11,
      letterSpacing: ".04em",
      textTransform: "uppercase",
      color: "var(--grey-500)",
      fontWeight: 600,
    }}
  >
    <span>{title}</span>
    {note && <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>{note}</span>}
  </Flex>
);

/** The right-hand cluster of a row: Picked tag, note pill, slot time. */
const RowAside = ({ option, size = "small" }: { option: ClientOption; size?: "small" | "large" }) => {
  const large = size === "large";
  return (
    <Flex gap={8} align="center" style={{ flexShrink: 0 }}>
      {option.picked && (
        <Tag color="geekblue" bordered={false} style={{ marginInlineEnd: 0 }}>
          Picked
        </Tag>
      )}
      {option.noteLabel && (
        <Tag color="success" bordered={false} style={{ marginInlineEnd: 0 }}>
          <CheckOutlined /> {option.noteLabel}
        </Tag>
      )}
      {option.slotLabel && (
        <span
          style={{
            fontSize: option.emphasizeSlot ? (large ? 14 : 13) : 12,
            fontWeight: option.emphasizeSlot ? 600 : 400,
            color: option.emphasizeSlot ? "var(--dark-text)" : "var(--grey-text)",
            whiteSpace: "nowrap",
          }}
        >
          {option.slotLabel}
        </span>
      )}
    </Flex>
  );
};

const ClientSelector = () => {
  const componentName = "createnotemodal";
  const dispatch = useAppDispatch();
  const isMobile = useIsMobileView();
  const { isEditNote } = useAppSelector((state) => state.appState);
  const { recordingDetail } = useAppSelector((state) => state.recordings);
  const { referIndividualBy } = useSpecialityReference();
  const schedulePick = useSchedulePickContext();

  // "all"-scope: a clinician can already record on a patient shared with them (the backend has
  // always allowed it), so this picker has to offer those patients too — owned-scope made shared
  // patients unreachable unless you first opened them from the Shared tab. Browsing reads the
  // sidebar's sections (one source); search is a server overlay that merges owned + shared.
  const { query, results, loading, error, belowMinChars, onSearch, retry, reset } = useClientSearch("all");
  const { browseClients, browseLoading, browseHasMore, browseError, ensureLoaded, loadMore, retryBrowse } =
    useClientBrowse("all");

  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const selectRef = useRef<BaseSelectRef>(null);

  // Resolve the selected patient's name even if they're off-page (no in-memory list dependency).
  const { name: selectedClientName } = useResolveClient({
    id: recordingDetail?.client_id,
    uuid: recordingDetail?.client_uuid,
    name: recordingDetail?.client_name,
  });

  const isBrowsing = !query.trim();
  const searchChars = query.trim().length;

  // Infinite scroll (dropdown popup + mobile drawer): nearing the bottom advances the shared
  // sidebar cursor. Search results are a single-page overlay.
  const onListScroll = useCallback(
    (e: UIEvent<HTMLDivElement>) => {
      if (!isBrowsing || !browseHasMore || browseLoading) return;
      const el = e.currentTarget;
      if (el.scrollHeight - el.scrollTop - el.clientHeight < 60) loadMore();
    },
    [isBrowsing, browseHasMore, browseLoading, loadMore],
  );

  const buildOption = useCallback(
    (
      client: TClientForm,
      extra: Pick<ClientOption, "source" | "rank"> &
        Partial<Pick<ClientOption, "slotLabel" | "noteLabel" | "emphasizeSlot" | "picked" | "muted">>,
    ): ClientOption => {
      const formattedDob = client.date_of_birth ? dayjs(client.date_of_birth).format("MM/DD/YYYY") : "";
      const derivedAgeFromDob =
        client.date_of_birth && formattedDob ? dayjs().diff(dayjs(client.date_of_birth), "year") : null;
      const rawAge =
        typeof client.age === "number" ? client.age : client.age ? Number.parseInt(client.age.toString(), 10) : null;
      const displayAge = formattedDob ? derivedAgeFromDob : rawAge;

      const secondaryDetails: string[] = [];
      if (formattedDob) {
        secondaryDetails.push(`DOB: ${formattedDob}`);
      } else if (displayAge !== null && !Number.isNaN(displayAge) && displayAge >= 0) {
        secondaryDetails.push(`Age: ${displayAge}`);
      }
      if (client.mrn) {
        secondaryDetails.push(`MRN/Patient ID: ${client.mrn}`);
      }

      return {
        value: client.id as number,
        optionLabel: client.name,
        label: (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <span>
              {client.name}{" "}
              {!client.is_owned && client.owner_name && (
                <span style={{ fontSize: 12, color: "var(--grey-text)" }}>- Shared by {client.owner_name}</span>
              )}
            </span>
            {secondaryDetails.length > 0 && (
              <span style={{ fontSize: 12, color: "var(--grey-text)" }}>{secondaryDetails.join(" · ")}</span>
            )}
          </div>
        ),
        secondaryDetails,
        isOwned: client.is_owned,
        ownerName: client.owner_name,
        client,
        slotLabel: "",
        noteLabel: "",
        emphasizeSlot: false,
        picked: false,
        muted: false,
        ...extra,
      };
    },
    [],
  );

  // Browsing with today's sheet known: the server's three groups, then the rest of the roster.
  const scheduleGroups: ScheduleGroup[] = useMemo(
    () =>
      isBrowsing && schedulePick.enabled
        ? buildScheduleGroups(schedulePick.pick, browseClients, {
            zone: schedulePick.clinicZone,
            autopickId: schedulePick.autopick?.clientId ?? null,
            allTitle: `All ${referIndividualBy.toLowerCase()}s`,
          })
        : [],
    [
      isBrowsing,
      schedulePick.enabled,
      schedulePick.pick,
      schedulePick.autopick,
      schedulePick.clinicZone,
      browseClients,
      referIndividualBy,
    ],
  );
  const hasScheduleGroups = scheduleGroups.some((g) => g.key !== "all");

  const groupOptions = useMemo(
    () =>
      scheduleGroups.map((group) => ({
        key: group.key,
        title: group.title,
        note: group.note,
        options: group.rows.map((row) =>
          buildOption(row.client, {
            source: row.source,
            rank: row.rank,
            slotLabel: row.slot ? scheduleSlotLabel(row.slot.scheduled_at, schedulePick.clinicZone) : "",
            noteLabel: row.slot?.documented_at
              ? `note ${scheduleSlotLabel(row.slot.documented_at, schedulePick.clinicZone)}`
              : "",
            emphasizeSlot: group.key === "recommended",
            picked: row.picked,
            muted: group.key === "documented",
          }),
        ),
      })),
    [scheduleGroups, buildOption, schedulePick.clinicZone],
  );

  // Flat list: search results, or the plain roster when there is no sheet to group by.
  const flatOptions: ClientOption[] = useMemo(() => {
    if (hasScheduleGroups) return [];
    const source: PatientPickSource = isBrowsing ? "browse" : "search";
    const rows = isBrowsing ? browseClients : results;
    return rows.map((client, i) => buildOption(client, { source, rank: i + 1 }));
  }, [hasScheduleGroups, isBrowsing, browseClients, results, buildOption]);

  const desktopOptions = useMemo(
    () =>
      hasScheduleGroups
        ? groupOptions.map((group) => ({
            key: group.key,
            label: <GroupHeader title={group.title} note={group.note} />,
            options: group.options,
          }))
        : flatOptions,
    [hasScheduleGroups, groupOptions, flatOptions],
  );

  const allOptions: ClientOption[] = useMemo(
    () => (hasScheduleGroups ? groupOptions.flatMap((g) => g.options) : flatOptions),
    [hasScheduleGroups, groupOptions, flatOptions],
  );

  // Selection goes through the schedule-pick controller so it can record where the choice came
  // from; without a provider (nothing in the app renders this picker bare) it falls back to the
  // plain dispatch.
  const chooseClient = useCallback(
    (client: TClientForm | undefined, source: PatientPickSource, rank?: number) => {
      if (schedulePick.provided) {
        schedulePick.choose(client, source, { rank, searchChars });
        return;
      }
      dispatch(
        setRecordingDetail({
          ...recordingDetail,
          client_id: client?.id,
          client_uuid: client?.uuid ?? null,
          client_name: client?.name ?? null,
        }),
      );
    },
    [schedulePick, searchChars, dispatch, recordingDetail],
  );

  const handleMobileClientSelect = useCallback(
    (option: ClientOption) => {
      chooseClient(option.client, option.source, option.rank);
      setIsMobileDrawerOpen(false);
      onSearch("");
    },
    [chooseClient, onSearch],
  );

  const handleAddClientMobile = useCallback(() => {
    setIsMobileDrawerOpen(false);
    onSearch("");
    dispatch(setAddClientView(true));
  }, [dispatch, onSearch]);

  // "Switch" on the recommendation bar opens the picker. Only a change of the counter counts:
  // the value it holds when this picker mounts is a request answered by an earlier mount.
  const seenOpenRequestRef = useRef(schedulePick.pickerOpenRequest);
  // biome-ignore lint/correctness/useExhaustiveDependencies: reacts to the request counter only
  useEffect(() => {
    if (schedulePick.pickerOpenRequest === seenOpenRequestRef.current) return;
    seenOpenRequestRef.current = schedulePick.pickerOpenRequest;
    ensureLoaded();
    if (isMobile) {
      setIsMobileDrawerOpen(true);
    } else {
      setDropdownOpen(true);
      selectRef.current?.focus();
    }
  }, [schedulePick.pickerOpenRequest]);

  // Shared loading/error/empty flags for both layouts (browse-aware when the query is empty).
  const listLoading = isBrowsing ? browseLoading && browseClients.length === 0 && !hasScheduleGroups : loading;
  const listError = isBrowsing ? browseError && browseClients.length === 0 && !hasScheduleGroups : error;
  const retryList = isBrowsing ? retryBrowse : retry;
  const emptyMessage = listLoading
    ? null
    : listError
      ? "error"
      : belowMinChars
        ? `Type 2+ letters to search by name…`
        : query.trim()
          ? `No ${referIndividualBy.toLowerCase()}s match "${query.trim()}"`
          : `No ${referIndividualBy.toLowerCase()}s yet`;

  // The schedule strip under the list: freshness + refresh. Only when browsing a synced account.
  const showScheduleStrip = isBrowsing && schedulePick.enabled && schedulePick.pick !== null;
  const syncing = schedulePick.refreshPhase === "running";
  const coolingDown = schedulePick.refreshPhase === "cooldown";
  const stripText = (() => {
    const pick = schedulePick.pick;
    if (!pick) return "";
    const parts = [`Schedule ${syncedLabel(pick, schedulePick.clinicZone)}`];
    if (pick.state === "ready") parts.push(`${pick.documented.length} of ${pick.schedule_size} documented`);
    parts.push(`type to search all ${referIndividualBy.toLowerCase()}s`);
    return parts.join(" · ");
  })();
  const refreshLabel = syncing
    ? "Syncing…"
    : coolingDown
      ? `Try again in ${cooldownLabel(schedulePick.refreshCooldownS)}`
      : "Refresh schedule";
  const scheduleStrip = showScheduleStrip ? (
    <Flex
      justify="space-between"
      align="center"
      gap={8}
      onMouseDown={(e) => e.preventDefault()}
      style={{
        padding: "6px 12px",
        borderTop: "1px solid var(--grey-200)",
        background: "var(--grey-50)",
        fontSize: 12,
        color: "var(--grey-500)",
      }}
    >
      <span>{stripText}</span>
      <Button
        type="link"
        size="small"
        icon={<ReloadOutlined spin={syncing} />}
        disabled={syncing || coolingDown}
        onClick={schedulePick.refresh}
        style={{ padding: 0, fontSize: 12 }}
      >
        {refreshLabel}
      </Button>
    </Flex>
  ) : null;

  // Mobile: trigger button + bottom drawer
  if (isMobile) {
    const renderMobileRow = (opt: ClientOption, keyPrefix: string) => {
      const clientName = opt.optionLabel || "";
      const isSelected = recordingDetail?.client_id === opt.value;
      return (
        <div
          key={`${keyPrefix}-${opt.value}`}
          onClick={() => handleMobileClientSelect(opt)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "8px 12px",
            borderBottom: "1px solid #f0f0f0",
            backgroundColor: isSelected ? "var(--indigo-50)" : "#fff",
            cursor: "pointer",
            minHeight: 48,
            opacity: opt.muted ? 0.6 : 1,
          }}
        >
          <Avatar style={{ backgroundColor: getRandomAvatarColor(clientName), flexShrink: 0 }} size={40}>
            {initialsOf(clientName)}
          </Avatar>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontWeight: isSelected ? 600 : 400,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {clientName}
              {!opt.isOwned && opt.ownerName && (
                <span style={{ fontSize: 12, color: "var(--grey-text)", marginLeft: 6 }}>
                  - Shared by {opt.ownerName}
                </span>
              )}
            </div>
            {opt.secondaryDetails.length > 0 && (
              <div
                style={{
                  fontSize: 13,
                  color: "var(--grey-text)",
                  marginTop: 2,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {opt.secondaryDetails.join(" · ")}
              </div>
            )}
          </div>
          <RowAside option={opt} size="large" />
          {isSelected && (
            <div
              style={{
                width: 20,
                height: 20,
                borderRadius: "50%",
                backgroundColor: "var(--primary)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <span style={{ color: "#fff", fontSize: 12, fontWeight: 700 }}>✓</span>
            </div>
          )}
        </div>
      );
    };

    return (
      <Col span={24} className={`${styles[`${componentName}__modal-row`]}`}>
        <Typography.Text>{referIndividualBy}:</Typography.Text>
        <Button
          onClick={() => {
            ensureLoaded();
            setIsMobileDrawerOpen(true);
          }}
          className={`${styles[`${componentName}__modal-row-field`]} ${styles[`${componentName}__modal-row-client-trigger`]}`}
          style={{
            width: "100%",
            height: 44,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            textAlign: "left",
            marginTop: 5,
            padding: "0 12px",
            borderColor: "var(--grey-200)",
          }}
        >
          <span
            style={{
              color: selectedClientName ? "inherit" : "#bfbfbf",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {selectedClientName || `Select a ${referIndividualBy.toLowerCase()}`}
          </span>
          <RightOutlined style={{ fontSize: 12, color: "var(--grey-text)" }} />
        </Button>

        <Drawer
          title={`Select ${referIndividualBy}`}
          placement="bottom"
          height="85%"
          open={isMobileDrawerOpen}
          onClose={() => {
            setIsMobileDrawerOpen(false);
            onSearch("");
          }}
          styles={{ body: { padding: 0, display: "flex", flexDirection: "column", overflow: "hidden" } }}
          closeIcon={<CloseOutlined />}
        >
          <div style={{ padding: "12px 16px", borderBottom: "1px solid #f0f0f0", backgroundColor: "#fff" }}>
            <Input
              prefix={<SearchOutlined style={{ color: "var(--grey-text)" }} />}
              placeholder={`Search by name, DOB, or MRN/Patient ID...`}
              value={query}
              onChange={(e) => onSearch(e.target.value)}
              allowClear
              size="large"
              style={{ borderRadius: 8 }}
              autoFocus
            />
          </div>

          {/* Scrollable client list (scrolls above the on-screen keyboard); paginates near the bottom. */}
          <div style={{ flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch" }} onScroll={onListScroll}>
            {listLoading ? (
              <div style={{ padding: "40px 16px", textAlign: "center" }} aria-busy="true">
                <Spin />
              </div>
            ) : listError ? (
              <div style={{ padding: "40px 16px", textAlign: "center", color: "#cf1322" }}>
                ⚠ Couldn't load — check your connection.{" "}
                <Button type="link" onClick={retryList}>
                  Retry
                </Button>
              </div>
            ) : allOptions.length === 0 ? (
              <div style={{ padding: "40px 16px", textAlign: "center", color: "var(--grey-text)" }}>
                <Typography.Text type="secondary">{emptyMessage}</Typography.Text>
              </div>
            ) : hasScheduleGroups ? (
              <>
                {groupOptions.map((group) => (
                  <div key={group.key}>
                    {group.title && (
                      <div style={{ padding: "10px 16px 4px", background: "#fafafa" }}>
                        <GroupHeader title={group.title} note={group.note} />
                      </div>
                    )}
                    {group.options.map((opt) => renderMobileRow(opt, group.key))}
                  </div>
                ))}
                {isBrowsing && <PickerListFooter loadingMore={browseLoading} error={browseError} retry={retryBrowse} />}
              </>
            ) : (
              <>
                {flatOptions.map((opt) => renderMobileRow(opt, "flat"))}
                {isBrowsing && <PickerListFooter loadingMore={browseLoading} error={browseError} retry={retryBrowse} />}
              </>
            )}
          </div>

          {scheduleStrip}

          {!isEditNote && (
            <div style={{ padding: "12px 16px", borderTop: "1px solid #f0f0f0", backgroundColor: "#fff" }}>
              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={handleAddClientMobile}
                block
                size="large"
                style={{ borderRadius: 8 }}
              >
                Add New {referIndividualBy}
              </Button>
            </div>
          )}
        </Drawer>
      </Col>
    );
  }

  // Desktop: server-backed Select.
  return (
    <Col span={24} className={`${styles[`${componentName}__modal-row`]}`}>
      <Typography.Text>{referIndividualBy}:</Typography.Text>
      <Select<number, SelectOption>
        ref={selectRef}
        value={recordingDetail?.client_id ?? undefined}
        listHeight={hasScheduleGroups ? 360 : 256}
        open={dropdownOpen}
        onChange={(value, option) => {
          const picked = Array.isArray(option) ? option[0] : option;
          const resolved = picked && "client" in picked ? picked : allOptions.find((o) => o.value === value);
          chooseClient(resolved?.client, resolved?.source ?? "browse", resolved?.rank);
        }}
        showSearch
        allowClear
        placeholder={`Select a ${referIndividualBy.toLowerCase()}`}
        filterOption={false}
        onSearch={onSearch}
        onDropdownVisibleChange={(open) => {
          setDropdownOpen(open);
          if (open) ensureLoaded();
          else reset();
        }}
        onPopupScroll={onListScroll}
        loading={loading}
        notFoundContent={
          listLoading ? (
            <div style={{ textAlign: "center", padding: 8 }} aria-busy="true">
              <Spin size="small" />
            </div>
          ) : listError ? (
            <div style={{ padding: 8, color: "#cf1322" }}>
              ⚠ Couldn't load — check your connection.{" "}
              <Button type="link" size="small" style={{ padding: 0 }} onClick={retryList}>
                Retry
              </Button>
            </div>
          ) : (
            <div style={{ padding: 8, color: "#8c8c8c" }}>{emptyMessage}</div>
          )
        }
        labelRender={() => selectedClientName ?? null}
        optionRender={(option) => {
          const data = option.data as ClientOption;
          const clientName = data?.optionLabel || "";
          return (
            <Flex gap={10} align="center" justify="space-between" style={{ opacity: data?.muted ? 0.55 : 1 }}>
              <Flex gap={10} align="center" style={{ minWidth: 0, flex: 1 }}>
                <Avatar style={{ backgroundColor: getRandomAvatarColor(clientName), flexShrink: 0 }} size="small">
                  {initialsOf(clientName)}
                </Avatar>
                <Typography.Text>{option.label}</Typography.Text>
              </Flex>
              {data && <RowAside option={data} />}
            </Flex>
          );
        }}
        dropdownRender={(menu) => (
          <div className="client">
            {menu}
            {isBrowsing && browseClients.length > 0 && (
              <PickerListFooter loadingMore={browseLoading} error={browseError} retry={retryBrowse} />
            )}
            {scheduleStrip}
            {!isEditNote ? (
              <>
                <Divider style={{ margin: "4px 0" }} />
                <Flex gap={10}>
                  <Button type="text" icon={<PlusOutlined />} onClick={() => dispatch(setAddClientView(true))}>
                    Add {referIndividualBy}
                  </Button>
                </Flex>
              </>
            ) : null}
          </div>
        )}
        className={`${styles[`${componentName}__modal-row-field`]}`}
        options={desktopOptions}
      />
    </Col>
  );
};

export default ClientSelector;
